import { readingHtml } from '../../shared/reading_request';
import consumer from "../../channels/consumer"
import AudioPlayerService from './audio_player_service'
import TranslationService from '../../contents/translation_service'

class GuidedModeService {
    // transliterate(element): puts Greek/Korean readings under the words in
    // element when the aid is on (LAN-870). ChatService supplies it.
    constructor(chatId, language, voice, voiceProvider, targetLanguage, bottomPopover, transliterate = null) {
        this.chatId = chatId
        this.language = language
        this.targetLanguage = targetLanguage
        this.voice = voice
        this.voiceProvider = voiceProvider
        this.overlay = document.getElementById("guided-mode-overlay")
        this.suggestionEl = document.getElementById("guided-mode-suggestion")
        this.translationEl = document.getElementById("guided-mode-translation")
        this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        this.bottomPopover = bottomPopover
        this.form = document.getElementById("js-chat-form")
        this.serverMediated = this.form?.dataset.serverMediatedAi === 'true'
        this.transitionInProgress = false;
        this.suggestionText = '';
        this.readingAidService = null;
        this.isReadingAidsOn = false;
        this.transliterate = transliterate;
        // Audio caching properties
        this.cachedAudioUrl = null;
        this.cachedSuggestionText = null;
        this.cachedPlaybackRate = null;
        // Initialize translation service
        this.translationService = new TranslationService(
            this.language,
            this.targetLanguage,
            this.csrfToken
        )
        this.isLoading = false
        this.loadingTimeout = null
        this.audioPlayer = new AudioPlayerService(this.csrfToken)
        this.audioBtn = document.getElementById("guided-mode-audio-btn")
        this.init()
    }

    init() {
        if (!this.overlay) return

        if (!this.serverMediated) this.setupActionCable()
        this.setupEventListeners()
        this.setupLoadingAnimation()
    }

    setReadingAidService(readingAidService, isReadingAidsOn) {
        this.readingAidService = readingAidService
        this.isReadingAidsOn = isReadingAidsOn
    }

    setupActionCable() {
        consumer.subscriptions.create(
            { channel: "ChatChannel", chat_id: this.chatId },
            {
                received: async (data) => {
                    if (data.type === "guided_mode") {
                        this.hideLoading()
                        await this.show(data.suggestion, data.translation)
                    }
                }
            }
        )
    }

    setupEventListeners() {
        if (this.form) {
            this.form.addEventListener("submit", (e) => {
                // Force the transition to run before form submission processing
                this.hide(true);
            });
        }

        if (this.audioBtn && !this.serverMediated) {
            this.audioBtn.addEventListener("click", () => this.playAudio())
        }

        // Add click handler for suggestion text
        if (this.suggestionEl) {
            this.suggestionEl.addEventListener('click', (e) => {
                // Check if the clicked element is a word span
                if (e.target.classList.contains('word')) {
                    this.handleWordClick(e.target.textContent, e.target)
                } else {
                    // Handle text selection
                    const selection = window.getSelection()
                    const selectedText = selection.toString().trim()

                    if (selectedText) {
                        this.handleWordClick(selectedText, e.target)
                    }
                }
            })
        }
    }

    setupLoadingAnimation() {
        // Create loading dots container
        const loadingContainer = document.createElement('div')
        loadingContainer.className = 'guided-mode-loading hidden'
        loadingContainer.innerHTML = `
            <div class="bouncing-dots">
                <div class="dot dot-1"></div>
                <div class="dot dot-2"></div>
                <div class="dot dot-3"></div>
            </div>
        `
        this.overlay.appendChild(loadingContainer)
        this.loadingContainer = loadingContainer
    }

    showLoading() {
        // Make sure overlay is visible and not in transition state
        this.overlay.classList.remove("hidden")
        this.overlay.classList.remove("fade-out")

        this.isLoading = true
        this.suggestionEl.textContent = ''
        this.translationEl.textContent = ''
        this.loadingContainer.classList.remove("hidden")

        // Hide other content while loading
        this.suggestionEl.style.display = 'none'
        this.translationEl.style.display = 'none'
        this.overlay.querySelector('.guided-mode-header').style.display = 'none'

        moveGuidedOverlayToEnd()

        // Start a 6-second timeout to cancel if no response
        this.clearLoadingTimeout()
        this.loadingTimeout = setTimeout(() => {
            if (this.isLoading) {
                this.hide(true)
            }
        }, 6000)
    }

    hideLoading() {
        this.isLoading = false
        this.loadingContainer.classList.add("hidden")

        // Show other content again
        this.suggestionEl.style.display = ''
        this.translationEl.style.display = ''
        this.overlay.querySelector('.guided-mode-header').style.display = ''

        this.clearLoadingTimeout()
    }

    async updateSuggestion(suggestion) {
        this.suggestionText = suggestion;
        let segments = [];
        if (this.readingAidService) {
            segments = await this.readingAidService.processText(suggestion, this.isReadingAidsOn);
        } else {
            // Wrap each word in an interactive span
            segments = suggestion.split(' ');
        }
        if (this.serverMediated && typeof segments === 'string') segments = [segments];
        const wrappedSegments = segments.map(segment => {
            if (segment.html) {
                return `<span class="js-translatable-text js-audio-word word" style="cursor: pointer;" data-translated-text="${this.serverMediated ? readingHtml(segment.text) : segment.text}" data-phrase="${this.serverMediated ? readingHtml(segment.text) : segment.text}">${segment.html}</span>`
            } else {
                return `<span class="js-translatable-text js-audio-word word" style="cursor: pointer;" data-translated-text="${this.serverMediated ? readingHtml(suggestion) : suggestion}">${this.serverMediated ? readingHtml(segment) : segment}</span>`
            }
        }).join(' ');

        // Add data-translated-text to the container for sentence translation
        this.suggestionEl.setAttribute('data-translated-text', suggestion)
        this.suggestionEl.innerHTML = wrappedSegments;
        if (this.transliterate) await this.transliterate(this.suggestionEl);
    }

    async show(suggestion, translation, owned = null) {
        if (this.serverMediated) {
            if (!owned || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(owned.messageId)) throw new Error('Invalid owned suggestion');
            this.audioBtn.classList.add('js-cartus-message-speech');
            this.audioBtn.dataset.chatMessageId = owned.messageId;
            this.audioBtn.dataset.suggestionIndex = '0';
            this.audioBtn.hidden = this.form.dataset.messageSpeechEnabled !== 'true';
        }
        this.hideLoading() // ensures timeout is cleared

        // Clear audio cache when showing a new suggestion
        this.clearAudioCache();

        // Ensure overlay is visible
        this.overlay.classList.remove("fade-out")
        this.overlay.classList.remove("hidden")

        await this.updateSuggestion(suggestion);
        
        this.translationEl.textContent = translation

        // Scroll to ensure the last message is visible above the overlay
        const chatContent = document.querySelector('.chat-content')
        if (chatContent) {
            const lastMessage = chatContent.querySelector('.chat-message:last-child')
            if (lastMessage) {
                lastMessage.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }
        }

        moveGuidedOverlayToEnd()
    }

    async updateGuidedModeOverlay() {
        await this.updateSuggestion(this.suggestionText);
    }

    hide(animate = false) {
        // Clear any pending timeouts
        this.clearLoadingTimeout();

        // Clear audio cache when hiding
        this.clearAudioCache();

        // Check if overlay exists
        if (!this.overlay) return;

        if (animate) {
            // Create placeholder to maintain space
            const placeholder = document.createElement('div');
            placeholder.style.height = `${this.overlay.offsetHeight}px`;
            placeholder.style.width = `${this.overlay.offsetWidth}px`;
            placeholder.style.transition = 'height 0.5s ease';
            placeholder.id = 'guided-mode-placeholder';

            // Insert the placeholder before the overlay
            if (this.overlay.parentNode) {
                this.overlay.parentNode.insertBefore(placeholder, this.overlay);
            }

            // Start fade-out transition
            this.transitionInProgress = true;
            this.overlay.classList.add("fade-out");

            // Add hidden class after a short delay
            setTimeout(() => {
                this.overlay.classList.add("hidden");
                this.overlay.classList.remove("fade-out");
                this.transitionInProgress = false;

                // Gradually reduce the placeholder height
                placeholder.style.height = '0';
                setTimeout(() => {
                    if (placeholder.parentNode) {
                        placeholder.parentNode.removeChild(placeholder);
                    }
                }, 500);
            }, 300);
        } else {
            // Immediately hide
            this.overlay.classList.add("hidden");
            this.overlay.classList.remove("fade-out");
        }

        this.hideLoading();
    }

    clearLoadingTimeout() {
        if (this.loadingTimeout) {
            clearTimeout(this.loadingTimeout)
            this.loadingTimeout = null
        }
    }

    // New method to handle guided mode suggestions
    async requestSuggestion(chatMessageId, text, context) {
        if (this.serverMediated) return null; // Rails already reserved the owned suggestion job.
        try {
            // Don't hide the overlay, just clear its content and show loading
            this.showLoading()
            moveGuidedOverlayToEnd()
            const response = await fetch('/langua/guided_mode_replies', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    chat_id: this.chatId,
                    chat_message_id: chatMessageId,
                    text: text,
                    language: this.language,
                    context: context
                })
            })

            if (!response.ok) {
                throw new Error('Failed to fetch guided mode suggestion')
            }

            return await response.json()
        } catch (error) {
            console.error('Error requesting guided mode suggestion:', error)
            this.hide(true) // Animate the hiding on error
            return null
        }
    }

    playAudio() {
        if (this.serverMediated) return; // Existing owned-message adapter handles this icon.
        const text = this.suggestionEl.dataset.translatedText || this.suggestionEl.textContent;
        if (!text) return;

        // Check if we have cached audio for this exact text
        if (this.cachedAudioUrl && this.cachedSuggestionText === text) {
            // Use cached audio
            if (this.audioPlayer.soundPlaying && this.audioPlayer.soundEl === this.audioBtn) {
                this.audioPlayer.togglePlayer()
            } else {
                // Play the cached audio
                this.audioPlayer.setupPlayer(this.cachedAudioUrl, this.audioBtn, this.cachedPlaybackRate || 1.0)
                this.audioPlayer.player.play()
            }
        } else {
            // No cached audio or text has changed, fetch new audio
            if (this.audioPlayer.soundPlaying && this.audioPlayer.soundEl === this.audioBtn) {
                this.audioPlayer.togglePlayer()
            } else {
                // Show loading state
                this.audioBtn.classList.add('pulse')

                // Fetch and cache the audio
                this.audioPlayer.fetchAudioData(text, this.language, {
                    voice_provider: this.voiceProvider,
                    voice: this.voice
                }).then(data => {
                    this.audioBtn.classList.remove('pulse')

                    // Cache the audio URL, text and Eleven v4 playback rate (LAN-1177)
                    this.cachedAudioUrl = data.audioUrl
                    this.cachedSuggestionText = text
                    this.cachedPlaybackRate = data.playbackRate

                    // Play the audio
                    this.audioPlayer.setupPlayer(data.audioUrl, this.audioBtn, data.playbackRate || 1.0)
                    this.audioPlayer.player.play()
                }).catch(error => {
                    console.error('Error fetching guided mode audio:', error)
                    this.audioBtn.classList.remove('pulse')
                })
            }
        }
    }

    clearAudioCache() {
        // Clean up any existing cached audio URL to prevent memory leaks
        if (this.cachedAudioUrl) {
            URL.revokeObjectURL(this.cachedAudioUrl);
        }
        this.cachedAudioUrl = null;
        this.cachedSuggestionText = null;
        this.cachedPlaybackRate = null;
    }

    handleWordClick(word, element) {
        // Get the full sentence from the container
        const sentence = this.suggestionEl.getAttribute('data-translated-text')

        // Use translation service to handle the translation
        this.translationService.translateWord(element, sentence, (translatedData) => {
            this.bottomPopover.updatePopoverContent(element, translatedData)
        })
    }
}

function moveGuidedOverlayToEnd() {
    const chatContent = document.querySelector('.chat-content')
    const overlay = document.getElementById('guided-mode-overlay')
    const placeholder = document.getElementById('guided-mode-placeholder')

    if (chatContent) {
        // Move the overlay to the end if it exists
        if (overlay && overlay.parentNode === chatContent) {
            chatContent.appendChild(overlay)
        }

        // Also move any placeholder to the end if it exists
        if (placeholder && placeholder.parentNode === chatContent) {
            chatContent.appendChild(placeholder)
        }
    }
}

export default GuidedModeService
